import { isQuebecListing } from '../../utils/is-quebec-listing';

type DisplayRestrictions = {
  hasTimestampRestrictions: boolean;
  hasSoldPriceRestrictions: boolean;
};

/**
 * Value object that encapsulates regional display policies for listings.
 * 
 * This class determines what listing information can be shown based on
 * regional regulations (e.g., Quebec's restrictions on sold price display).
 */
export class ListingDisplayPolicy {
  readonly #hasTimestampRestrictions: boolean;
  readonly #hasSoldPriceRestrictions: boolean;

  private constructor({ hasTimestampRestrictions, hasSoldPriceRestrictions }: DisplayRestrictions) {
    this.#hasTimestampRestrictions = hasTimestampRestrictions;
    this.#hasSoldPriceRestrictions = hasSoldPriceRestrictions;
  }

  /**
   * Creates a ListingDisplayPolicy instance from listing properties.
   */
  static fromListing(province: string | null | undefined, isSold: boolean): ListingDisplayPolicy {
    const isQuebec = isQuebecListing(province);
    return new ListingDisplayPolicy({
      hasTimestampRestrictions: isQuebec, // Quebec has timestamp restrictions
      hasSoldPriceRestrictions: isQuebec && isSold, // Quebec sold listings have price restrictions
    });
  }

  /**
   * Whether the sold price can be displayed.
   */
  get canDisplaySoldPrice(): boolean {
    return !this.#hasSoldPriceRestrictions;
  }

  /**
   * Whether timestamps (addedAt, soldAt, etc.) can be displayed.
   */
  get canDisplayTimestamps(): boolean {
    return !this.#hasTimestampRestrictions;
  }

  /**
   * Whether a sold price value is displayable (has value and policy allows it).
   */
  shouldShowSoldPrice(soldPrice: number | null): boolean {
    return this.canDisplaySoldPrice && soldPrice !== null && soldPrice > 0;
  }

  /**
   * Gets the display value for a sold price.
   * Returns the formatted price if it can be displayed, otherwise returns a placeholder.
   * 
   * @param soldPrice - The actual sold price value
   * @param formatCurrency - Function to format the price as currency
   * @returns The displayable string (formatted price or placeholder)
   */
  getDisplayableSoldPrice(soldPrice: number | null, formatCurrency: (value: number) => string): string {
    if (!this.shouldShowSoldPrice(soldPrice)) {
      return '$---';
    }
    return formatCurrency(soldPrice);
  }

  /**
   * Gets the display value for a price.
   * 
   * @param price - The actual price value
   * @param formatCurrency - Function to format the price as currency
   * @returns The displayable string (formatted price or placeholder)
   */
  getDisplayablePrice(price: number | null, formatCurrency: (value: number) => string): string {
    if (price === null || price === 0) {
      return '$---';
    }
    return formatCurrency(price);
  }
}
